import AuthForm from '@/components/features/auth/AuthForm'
import AuthPage from '@/components/features/auth/AuthPage'
import {
  ConfirmationCodeController,
  TextController,
} from '@/components/forms/FormController'
import ErrorMessage from '@/components/utils/ErrorMessage'
import { useAuth } from '@/context/AuthContext'
import { useConfig } from '@/context/ConfigContext'
import { isAlreadyConfirmedError, isUserNotFoundError } from '@/lib/auth-errors'
import { confirmationCodeSchema } from '@/lib/confirmation-code'
import { resolveUsername } from '@/lib/resolve-username'
import { zodResolver } from '@/lib/zod'
import { CheckIcon } from '@heroicons/react/24/outline'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useCountdown } from 'usehooks-ts'
import { z } from 'zod'

import { confirmSignUp, resendSignUpCode } from '@aws-amplify/auth'

import { Button } from '@droidmap/frontend-ui'
import { Separator } from '@droidmap/frontend-ui'
import { Spinner } from '@droidmap/frontend-ui'

const confirmSignUpSchema = z.object({
  username: z.string().min(1, 'Username or email address is required'),
  confirmationCode: confirmationCodeSchema,
})

// Both params are optional — the page is reachable cold (no search at all), and
// `.catch(undefined)` keeps a malformed value (e.g. `?username[]=a`, which
// arrives as an array) degrading to that same cold form rather than throwing
// the user into the router's error boundary. This route is linked from a
// verification email, so we don't control how the URL arrives.
const confirmSignUpSearchSchema = z.object({
  username: z.string().optional().catch(undefined),
  code: z.string().optional().catch(undefined),
})

export const Route = createFileRoute('/_unauthenticated/confirm-signup')({
  validateSearch: confirmSignUpSearchSchema,
  component: ConfirmSignUp,
})

type ConfirmSignUpFormValues = z.infer<typeof confirmSignUpSchema>
type VerificationState = 'idle' | 'verifying' | 'verified'

function CenteredContent({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center mt-8">
      {children}
    </div>
  )
}

export function ConfirmSignUp() {
  const auth = useAuth()
  const { COGNITO_SERVICE_URL } = useConfig()
  const navigate = useNavigate()
  const search = Route.useSearch()
  const code = search.code
  // Treat a blank/whitespace-only `?username=` as absent: locking a static
  // empty identifier would disable Resend code and submit '' on Confirm. The
  // trimmed value is what we use — signup rejects whitespace in usernames
  // (`signup.tsx:24`), so padding is always a URL artefact Cognito would 404 on.
  const username = search.username?.trim() || undefined

  const [verificationState, setVerificationState] = useState<VerificationState>(
    username && code ? 'verifying' : 'idle',
  )
  const [error, setError] = useState('')
  const [resendLoading, setResendLoading] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const hasAttemptedAutoVerify = useRef(false)
  // When we already know who is confirming, the identifier is rendered as
  // static text rather than a text input. A field literally named `username` on
  // this origin is a textbook autofill target, and a password manager filling a
  // *different* saved account here confirms the wrong user — silently, because
  // the user is looking at the code field. Removing the input removes the
  // target on every path a real user takes (signup redirect, verification
  // email, sign-in redirect); "Not you?" is the escape hatch. See #2405.
  const [isUsernameEditable, setIsUsernameEditable] = useState(!username)
  // Whether auto sign-in gave the user a live session. Only the
  // COMPLETE_AUTO_SIGN_IN step can, and only when autoSignIn() succeeds — the
  // already-confirmed shortcut below never has a session to inherit (#2040).
  const [isAutoSignedIn, setIsAutoSignedIn] = useState(false)

  // With a session there is nothing to do at /login — it would just forward
  // an authenticated user into the app, flashing a login screen on the way
  // (#998) — so go straight to the authenticated landing route, and quickly:
  // there is nothing to wait 15 seconds for.
  const redirectTo = isAutoSignedIn ? '/orgs' : '/login'

  const [countdown, { startCountdown, resetCountdown }] = useCountdown({
    countStart: isAutoSignedIn ? 3 : 15,
    intervalMs: 1000,
    isIncrement: false,
    countStop: 0,
  })

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ConfirmSignUpFormValues>({
    resolver: zodResolver(confirmSignUpSchema),
    defaultValues: {
      username: username ?? '',
      confirmationCode: code ?? '',
    },
    mode: 'onBlur',
  })

  // `useWatch` rather than `watch()`: it subscribes to just these two fields
  // and returns a plain value, where `watch()` returns a function whose identity
  // react-hook-form cannot keep stable — which is why the React Compiler skips
  // any component that calls it.
  const formUsername = useWatch({ control, name: 'username' })
  const confirmationCode = useWatch({ control, name: 'confirmationCode' })

  // The confirm field accepts a username OR an email. We try the identifier
  // as-is first — usernames resolve directly (including the auto-verify
  // email-link path and usernames that legally contain '@'). Only when Cognito
  // reports the user doesn't exist AND the identifier looks like an email do we
  // resolve the email to its real username server-side and retry once. Guessing
  // email-vs-username from the string alone is unsound because usernames can
  // contain '@'. See #2120.
  const withUsernameFallback = useCallback(
    async <T,>(
      identifier: string,
      action: (username: string) => Promise<T>,
    ): Promise<T> => {
      try {
        return await action(identifier)
      } catch (err) {
        if (isUserNotFoundError(err) && identifier.includes('@')) {
          const username = await resolveUsername(
            identifier,
            COGNITO_SERVICE_URL,
          )
          return action(username)
        }
        throw err
      }
    },
    [COGNITO_SERVICE_URL],
  )

  // Is this the identifier the page was handed, rather than one the user (or
  // their password manager) put into the field? With no `?username=` we
  // supplied nothing, so nothing can match and this is false — a hand-typed
  // identifier carries no evidence about which account it belongs to.
  //
  // Note this compares the identifier as *submitted*, which `withUsernameFallback`
  // may since have resolved to a different username before Cognito saw it. That
  // is still sound: a resolve only runs after `UserNotFoundException`, and
  // `resolveUsername` answers for UNCONFIRMED accounts only — so an
  // already-confirmed on the retry is by construction the account we were
  // pointed at, confirmed in the moment between the two calls. The #2040 race
  // exactly.
  const isIdentifierWeSupplied = useCallback(
    (identifier: string) => !!username && identifier.trim() === username,
    [username],
  )

  const onSubmit = useCallback(
    async (data: ConfirmSignUpFormValues) => {
      setError('')
      setSuccessMessage('')
      setVerificationState('verifying')

      try {
        const { nextStep } = await withUsernameFallback(
          data.username,
          (username) =>
            confirmSignUp({
              username,
              confirmationCode: data.confirmationCode,
            }),
        )

        if (nextStep?.signUpStep === 'COMPLETE_AUTO_SIGN_IN') {
          try {
            const { isSignedIn } = await auth.autoSignIn()
            setIsAutoSignedIn(isSignedIn)
          } catch {
            // The confirmation itself succeeded; without a session the
            // success screen falls back to the login redirect.
          }
        }

        setVerificationState('verified')
      } catch (err) {
        // Email verification is idempotent. The account can already be
        // confirmed before this submit — the verification email's confirm
        // link is a GET that a mail-security scanner / preview pane can fetch
        // (and so confirm the user) before they enter the code by hand. Treat
        // "already confirmed" as success rather than surfacing Cognito's
        // "Current status is CONFIRMED" error. See #2040.
        //
        // Cognito's error names no account, though, so it cannot on its own
        // tell that race apart from a code submitted against a *different*,
        // already-confirmed account — where the user's own account is untouched
        // and still UNCONFIRMED. Rendering success there tells them they're
        // done, drops them on a login that rejects them as nonexistent, and
        // sends no welcome email. See #2406.
        //
        // Provenance is the only signal that separates the two: a scanner can
        // only have confirmed the account whose link it fetched, so the
        // shortcut is sound exactly for the identifier *we* put on the page and
        // for no other. Every path a real user arrives by supplies it — the
        // signup redirect (`signup.tsx:70`), the verification email
        // (`EmailVerification.tsx:27`) and the two unconfirmed sign-in
        // redirects (`SignInForm/index.tsx:56`, `SendOTPForm/index.tsx:126`) —
        // so #2040 stays covered everywhere it can actually occur. A hand-typed
        // identifier (cold entry, or the "Not you?" escape hatch an autofill
        // can still swap a saved account into) gets an error naming the account
        // instead of a success screen nothing here can justify.
        if (
          isAlreadyConfirmedError(err) &&
          isIdentifierWeSupplied(data.username)
        ) {
          setVerificationState('verified')
          return
        }
        const errorMessage = isAlreadyConfirmedError(err)
          ? `${data.username} is already confirmed, so your code was not applied. If that is your account you can sign in below — otherwise check the username or email address you signed up with.`
          : err instanceof Error
            ? err.message
            : 'An unknown error occurred'
        setError(errorMessage)
        hasAttemptedAutoVerify.current = false
        setVerificationState('idle')
      }
    },
    [auth, isIdentifierWeSupplied, withUsernameFallback],
  )

  useEffect(() => {
    if (username && code && !hasAttemptedAutoVerify.current) {
      hasAttemptedAutoVerify.current = true
      onSubmit({ username, confirmationCode: code })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // The countdown starts here rather than in onSubmit: its length depends on
  // isAutoSignedIn, which lands in the same render as 'verified', and
  // resetCountdown only picks up the new countStart after that render. Layout
  // effect so the first painted frame shows that start value, not a flash of
  // the other path's.
  useLayoutEffect(() => {
    if (verificationState === 'verified') {
      resetCountdown()
      startCountdown()
    }
  }, [verificationState, resetCountdown, startCountdown])

  useEffect(() => {
    if (verificationState === 'verified' && countdown === 0) {
      navigate({ to: redirectTo })
    }
  }, [verificationState, countdown, navigate, redirectTo])

  const handleResendConfirmationCode = async () => {
    if (!formUsername) return

    setError('')
    setSuccessMessage('')
    setResendLoading(true)
    reset({ username: formUsername, confirmationCode: '' })

    try {
      await withUsernameFallback(formUsername, (username) =>
        resendSignUpCode({ username }),
      )
      setSuccessMessage('Confirmation code resent successfully')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred')
    } finally {
      setResendLoading(false)
    }
  }

  if (verificationState === 'verifying') {
    return (
      <AuthPage title="Verifying your account" className="mt-6">
        <CenteredContent>
          <div className="relative mb-6">
            <Spinner size="page" className="relative" />
          </div>
          <div className="text-center space-y-3">
            <p className="text-xl font-semibold">Almost there!</p>
            <p className="text-base">
              We're verifying your account, this will only take a moment...
            </p>
          </div>
        </CenteredContent>
      </AuthPage>
    )
  }

  if (verificationState === 'verified') {
    return (
      <AuthPage title="Email Verified" className="mt-6">
        <CenteredContent>
          <div className="bg-success/15 ring-success/30 mb-8 flex h-16 w-16 items-center justify-center rounded-full ring-1">
            <CheckIcon className="text-success h-10 w-10" />
          </div>
          <div className="text-center space-y-4 mb-8">
            <p className="text-xl font-semibold">
              Thanks for confirming your email!
            </p>
            <p className="text-base">
              {isAutoSignedIn
                ? "The user signup process is now complete and you're signed in."
                : 'The user signup process is now complete.'}
            </p>
            <p className="text-base">
              {isAutoSignedIn
                ? 'Taking you to your organisations in'
                : 'Redirecting to the login page in'}{' '}
              <span className="font-semibold">{countdown}</span>{' '}
              {countdown === 1 ? 'second' : 'seconds'}...
            </p>
          </div>
          <Button
            onClick={() => navigate({ to: redirectTo })}
            className="w-full max-w-xs"
          >
            Redirect Now
          </Button>
        </CenteredContent>
      </AuthPage>
    )
  }

  const isResendDisabled = resendLoading || !formUsername?.trim()
  const isConfirmDisabled =
    isSubmitting || resendLoading || !confirmationCode?.trim?.()

  return (
    <AuthPage title="Confirm Signup">
      {/* Built inside the submit event rather than during render: `onSubmit`
          reaches a ref, and handing a ref-touching function to react-hook-form
          while rendering is what `react-hooks/refs` flags. */}
      <AuthForm onSubmit={(event) => void handleSubmit(onSubmit)(event)}>
        {isUsernameEditable ? (
          <TextController
            name="username"
            control={control}
            label="Username / Email address"
            errors={errors}
            // Explicit: with the attribute omitted the browser falls back to
            // heuristics and fills a saved credential over the top. See #2405.
            autoComplete="off"
          />
        ) : (
          <div className="bg-panel border-border rounded-lg border p-4">
            <div className="text-micro text-text-dim mb-1">
              Confirming account
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-text truncate text-base font-semibold">
                {formUsername}
              </span>
              <button
                type="button"
                onClick={() => setIsUsernameEditable(true)}
                className="text-accent hover:text-accent-hi shrink-0 text-sm font-semibold"
              >
                Not you?
              </button>
            </div>
          </div>
        )}
        {/* "inbox", not "email". On the `?username=` path this hint and the
            field's label are the only text next to the only input on the page,
            and a `<label for>` containing an autofill keyword is the heaviest
            signal a password-manager classifier reads — heavier than the type
            or the length. `data-lpignore` asks LastPass to leave the field
            alone; keeping "email" off it removes the reason it wanted the
            field at all. (#2882) */}
        <p className="text-text-mute text-sm">
          Check your inbox for the code we just sent.
        </p>
        {/* Six numeric characters, capped and sanitised on input — the
            shape lever after the vendor opt-outs alone didn't stop LastPass
            filling this field. See ConfirmationCodeController. (#2882) */}
        <ConfirmationCodeController
          name="confirmationCode"
          control={control}
          label="6-digit confirmation code"
          errors={errors}
        />
        {error && <ErrorMessage content={error} />}
        {successMessage && (
          <div className="bg-success/10 border-success/30 text-success rounded-md border p-3 text-center text-sm">
            {successMessage}
          </div>
        )}

        <div className="flex gap-3 mt-4">
          <Button
            type="button"
            onClick={handleResendConfirmationCode}
            className="flex-1"
            disabled={isResendDisabled}
            variant="secondary"
            isLoading={resendLoading}
          >
            Resend code
          </Button>
          <Button
            type="submit"
            className="flex-1"
            isLoading={isSubmitting}
            disabled={isConfirmDisabled}
          >
            Confirm
          </Button>
        </div>

        <div className="mt-6">
          <Separator label="OR" />
        </div>

        <div className="mt-6">
          <Button
            variant="secondary"
            onClick={() => navigate({ to: '/login' })}
            className="w-full"
          >
            Sign in
          </Button>
        </div>
      </AuthForm>
    </AuthPage>
  )
}
